import { motion, useInView } from "framer-motion";
import { useRef } from "react";

/**
 * Dividend Calendar — animated payout-stream infographic.
 * Monthly bars rise; coins drop into a cumulative income jar.
 */
const DividendCalendarPreview = () => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  const months = [
    { m: "Jan", v: 32 },
    { m: "Feb", v: 48 },
    { m: "Mar", v: 41 },
    { m: "Apr", v: 64 },
    { m: "May", v: 55 },
    { m: "Jun", v: 78 },
    { m: "Jul", v: 62 },
    { m: "Aug", v: 84 },
    { m: "Sep", v: 71 },
    { m: "Oct", v: 92 },
    { m: "Nov", v: 86 },
    { m: "Dec", v: 100 },
  ];

  const total = months.reduce((s, m) => s + m.v, 0);

  return (
    <motion.div
      ref={ref}
      className="relative w-full max-w-[460px] mx-auto"
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
    >
      <div className="relative rounded-[2rem] bg-card border border-border/60 shadow-2xl p-7 overflow-hidden">
        <div
          className="absolute inset-0 opacity-60 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at bottom, hsl(var(--brand-clay) / 0.10), transparent 60%)",
          }}
        />

        {/* Header */}
        <div className="relative flex items-end justify-between mb-6">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
              Forecasted income
            </p>
            <div className="flex items-baseline gap-1.5 mt-1">
              <span className="text-3xl font-extrabold text-foreground tabular-nums">
                ${total.toLocaleString()}
              </span>
              <span className="text-xs font-semibold text-muted-foreground">/ yr</span>
            </div>
          </div>
          <motion.div
            className="text-right"
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ delay: 0.6 }}
          >
            <span
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold"
              style={{
                background: "hsl(var(--primary) / 0.12)",
                color: "hsl(var(--primary))",
              }}
            >
              ▲ +12.4% YoY
            </span>
            <p className="text-[10px] text-muted-foreground mt-1.5 font-medium">
              4 payouts this month
            </p>
          </motion.div>
        </div>

        {/* Chart */}
        <div className="relative h-[180px] flex items-end gap-1.5">
          {/* baseline */}
          <div className="absolute inset-x-0 bottom-0 border-t border-dashed border-border/70" />
          <div className="absolute inset-x-0 bottom-1/2 border-t border-dashed border-border/40" />

          {months.map((m, i) => {
            const isPeak = m.v === Math.max(...months.map((x) => x.v));
            return (
              <div key={m.m} className="flex-1 flex flex-col items-center gap-1.5 relative h-full justify-end">
                {/* bar */}
                <motion.div
                  className="w-full rounded-t-md relative overflow-hidden"
                  style={{
                    background: isPeak
                      ? "linear-gradient(180deg, hsl(var(--brand-amber)), hsl(var(--primary)))"
                      : "linear-gradient(180deg, hsl(var(--primary) / 0.85), hsl(var(--primary) / 0.55))",
                  }}
                  initial={{ height: 0 }}
                  animate={inView ? { height: `${m.v}%` } : {}}
                  transition={{
                    delay: 0.3 + i * 0.06,
                    duration: 0.9,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                >
                  {/* shimmer */}
                  <motion.div
                    className="absolute inset-0"
                    style={{
                      background:
                        "linear-gradient(180deg, hsl(var(--primary-foreground) / 0.35), transparent 40%)",
                    }}
                    animate={{ opacity: [0.3, 0.7, 0.3] }}
                    transition={{ duration: 2.4, repeat: Infinity, delay: i * 0.08 }}
                  />
                </motion.div>

                {/* peak marker */}
                {isPeak && (
                  <motion.div
                    className="absolute -top-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold whitespace-nowrap"
                    style={{
                      background: "hsl(var(--brand-amber))",
                      color: "hsl(var(--brand-forest-deep))",
                    }}
                    initial={{ opacity: 0, y: -4 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 1.4 }}
                  >
                    Peak
                  </motion.div>
                )}

                {/* month label */}
                <motion.span
                  className={`text-[9px] font-bold uppercase tracking-wider ${
                    isPeak ? "text-foreground" : "text-muted-foreground"
                  }`}
                  initial={{ opacity: 0 }}
                  animate={inView ? { opacity: 1 } : {}}
                  transition={{ delay: 0.6 + i * 0.04 }}
                >
                  {m.m}
                </motion.span>
              </div>
            );
          })}
        </div>

        {/* Coin stream into total */}
        <div className="relative mt-6 pt-5 border-t border-border/60 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-extrabold border-2"
                style={{
                  background: "hsl(var(--brand-amber) / 0.18)",
                  borderColor: "hsl(var(--brand-amber))",
                  color: "hsl(var(--brand-amber))",
                  marginLeft: i === 0 ? 0 : -10,
                }}
                initial={{ y: -20, opacity: 0 }}
                animate={inView ? { y: 0, opacity: 1 } : {}}
                transition={{ delay: 1.5 + i * 0.15, type: "spring", stiffness: 200 }}
              >
                $
              </motion.div>
            ))}
            <div className="ml-2">
              <p className="text-[11px] font-bold text-foreground leading-tight">Next 30 days</p>
              <p className="text-[10px] text-muted-foreground">AAPL · JNJ · KO · MSFT</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-base font-extrabold text-foreground tabular-nums">$184.20</p>
            <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold">
              Incoming
            </p>
          </div>
        </div>
      </div>

      <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 w-2/3 h-20 bg-primary/30 blur-3xl rounded-full -z-10" />
    </motion.div>
  );
};

export default DividendCalendarPreview;
